import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { B2BCompany, ModalService, Price, Product } from 'npx-family-happy-common';
import { Subject, catchError, forkJoin, of, takeUntil } from 'rxjs';
import { BusinessLine } from 'src/app/shared/models/businessLine-model';
import { B2BCompanyCatalogService } from './b2b-company-catalog.service';
import { B2BCompanyConventionService } from './b2b-company-convention.service';
import { CatalogConventionModalComponent } from './convention-modal/catalog-convention-modal.component';
import {
  Convention,
  ConventionRule,
  STATUS_LABELS,
  isB2BService,
  ruleFor,
  rulePriceText,
  ruleQuantityText,
  validityText,
} from './convention.model';
import { CatalogRuleModalComponent } from './rule-modal/catalog-rule-modal.component';
import {
  AREAS,
  AREA_ORDER,
  AreaId,
  CatalogFHService,
  CatalogProduct,
  CatalogService,
  CatalogSummary,
  PROVINCES,
  PriceGroup,
  errorMessage,
  euro,
  groupPrices,
  lineOf,
  productTypeLabel,
  provinceLabel,
} from './catalog.model';
import { CatalogPriceModalComponent } from './price-modal/catalog-price-modal.component';
import { CatalogProductModalComponent } from './product-modal/catalog-product-modal.component';
import { CatalogServiceModalComponent } from './service-modal/catalog-service-modal.component';

interface ProductModalState {
  item: CatalogService;
  product: Product | null;
  priceCount: number;
}

interface RuleModalState {
  item: CatalogService;
  product: Product;
}

interface PriceModalState {
  item: CatalogService;
  product: Product;
  price: Price | null;
}

/**
 * Convenzione e servizi collegati all'azienda, con prodotti e prezzi. Ogni azione salva subito
 * (servizi, prodotti, prezzi, convenzione): non c'è un salvataggio unico della pagina.
 *
 * I prodotti dei servizi B2B non hanno prezzi di listino: il loro prezzo (intervallo min–max) sta
 * nelle regole della convenzione e quello esatto si definisce sulla richiesta.
 */
@Component({
  selector: 'app-b2b-company-catalog',
  templateUrl: './b2b-company-catalog.component.html',
  styleUrls: ['./b2b-company-catalog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    CatalogServiceModalComponent,
    CatalogProductModalComponent,
    CatalogPriceModalComponent,
    CatalogRuleModalComponent,
    CatalogConventionModalComponent,
  ],
})
export class B2BCompanyCatalogComponent implements OnInit, OnDestroy {
  @Input({ required: true }) company!: B2BCompany;
  @Output() summaryChange = new EventEmitter<CatalogSummary>();

  readonly areas = AREAS;
  readonly areaOrder = AREA_ORDER;
  readonly lineOf = lineOf;
  readonly euro = euro;
  readonly productTypeLabel = productTypeLabel;
  readonly provinceLabel = provinceLabel;
  readonly isB2B = isB2BService;
  readonly rulePriceText = rulePriceText;
  readonly ruleQuantityText = ruleQuantityText;
  readonly validityText = validityText;
  readonly statusLabels = STATUS_LABELS;

  items: CatalogService[] = [];
  /** null = l'azienda non ha una convenzione. */
  convention: Convention | null = null;
  /** La lettura della convenzione è fallita: il catalogo si mostra comunque. */
  conventionError = '';
  loading = true;
  loadError = '';
  area: AreaId | 'all' = 'all';
  query = '';
  /** Prodotti con la lista dei prezzi aperta. */
  expanded = new Set<number>();
  /** Servizi con il cambio attivo/disattivo in corso. */
  toggling = new Set<number>();

  serviceModal: { service: CatalogFHService | null } | null = null;
  productModal: ProductModalState | null = null;
  priceModal: PriceModalState | null = null;
  ruleModal: RuleModalState | null = null;
  conventionModal = false;

  private unsubscribe = new Subject<void>();

  constructor(
    private catalog: B2BCompanyCatalogService,
    private conventions: B2BCompanyConventionService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.loading = !this.items.length;
    this.loadError = '';
    this.conventionError = '';
    const convention = this.conventions.getByCompany(this.company.id).pipe(
      catchError((error) => {
        this.conventionError = errorMessage(error, 'Impossibile caricare la convenzione.');
        return of(this.convention);
      })
    );
    forkJoin([this.catalog.load(this.company.id), convention])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: ([items, conv]) => {
          this.items = items;
          this.convention = conv;
          this.loading = false;
          this.emitSummary();
        },
        error: (error) => {
          this.loading = false;
          this.loadError = errorMessage(error, 'Impossibile caricare servizi, prodotti e prezzi.');
        },
      });
  }

  private emitSummary() {
    const products = this.items.reduce((sum, item) => sum + item.products.length, 0);
    const prices = this.items.reduce(
      (sum, item) => sum + item.products.reduce((acc, p) => acc + p.prices.length, 0),
      0
    );
    const rules = this.items
      .filter((item) => isB2BService(item.service))
      .reduce((sum, item) => sum + item.products.filter((p) => this.ruleOf(p.product)).length, 0);
    this.summaryChange.emit({
      services: this.items.length,
      activeServices: this.items.filter((item) => !item.service.disabled).length,
      products,
      prices,
      rules,
    });
  }

  /* --- Filtri ------------------------------------------------------------------------------------ */

  areaOf(item: CatalogService): AreaId {
    return lineOf(item.service.businessLine).area;
  }

  countFor(area: AreaId | 'all') {
    return area === 'all' ? this.items.length : this.items.filter((item) => this.areaOf(item) === area).length;
  }

  get visibleItems(): CatalogService[] {
    const q = this.query.trim().toLowerCase();
    return this.items.filter((item) => {
      if (this.area !== 'all' && this.areaOf(item) !== this.area) return false;
      if (!q) return true;
      return (
        item.service.name.toLowerCase().includes(q) ||
        lineOf(item.service.businessLine).label.toLowerCase().includes(q) ||
        item.products.some((p) => p.product.name.toLowerCase().includes(q))
      );
    });
  }

  get linkedIds() {
    return this.items.map((item) => item.service.id);
  }

  /* --- Convenzione -------------------------------------------------------------------------------- */

  ruleOf(product: Product): ConventionRule | null {
    return ruleFor(this.convention, product.id);
  }

  /** Nel caregiving la prestazione si chiama "selezione", negli altri servizi "seduta". */
  isCaregiving(service: CatalogFHService) {
    return lineOf(service.businessLine).area === 'caregiving';
  }

  get hasB2BServices() {
    return this.items.some((item) => isB2BService(item.service));
  }

  /** Linee dei servizi collegati, per il monte ore condiviso. */
  get companyLines(): BusinessLine[] {
    return Array.from(new Set(this.items.map((item) => item.service.businessLine as unknown as BusinessLine)));
  }

  poolLinesText(convention: Convention): string {
    const lines = convention.sharedHoursPool?.businessLines ?? [];
    return lines.length ? lines.map((line) => lineOf(line).label).join(', ') : 'nessun servizio';
  }

  /* --- Prezzi ------------------------------------------------------------------------------------ */

  priceGroups(row: CatalogProduct): PriceGroup[] {
    return groupPrices(row.prices);
  }

  /** "tutte le province", "Milano, Roma" oppure "Milano, Roma +3". */
  provincesText(group: PriceGroup): string {
    if (group.provinces.length === PROVINCES.length) return 'tutte le province';
    const labels = group.provinces.map((p) => provinceLabel(p));
    return labels.length <= 2 ? labels.join(', ') : `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
  }

  sortedPrices(row: CatalogProduct): Price[] {
    const order = PROVINCES.map((p) => p.value);
    return [...row.prices].sort((a, b) => order.indexOf(a.province) - order.indexOf(b.province));
  }

  toggleExpanded(productId: number) {
    if (this.expanded.has(productId)) this.expanded.delete(productId);
    else this.expanded.add(productId);
  }

  /* --- Azioni ------------------------------------------------------------------------------------ */

  toggleService(item: CatalogService) {
    const service = item.service;
    if (this.toggling.has(service.id)) return;
    this.toggling.add(service.id);
    service.disabled = !service.disabled;
    this.catalog
      .saveService(service, this.company)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          this.toggling.delete(service.id);
          this.emitSummary();
        },
        error: (error) => {
          service.disabled = !service.disabled;
          this.toggling.delete(service.id);
          this.modalService.showErrorMessage(errorMessage(error, 'Impossibile aggiornare il servizio.'));
        },
      });
  }

  openService(service: CatalogFHService | null = null) {
    this.serviceModal = { service };
  }

  openProduct(item: CatalogService, row: CatalogProduct | null = null) {
    this.productModal = { item, product: row?.product ?? null, priceCount: row?.prices.length ?? 0 };
  }

  openPrice(item: CatalogService, product: Product, price: Price | null = null) {
    this.expanded.add(product.id);
    this.priceModal = { item, product, price };
  }

  /** Senza convenzione si parte dalla sua creazione (servono le date), poi si imposta il prezzo. */
  openRule(item: CatalogService, product: Product) {
    if (!this.convention) {
      this.conventionModal = true;
      return;
    }
    this.ruleModal = { item, product };
  }

  closeModals() {
    this.serviceModal = null;
    this.productModal = null;
    this.priceModal = null;
    this.ruleModal = null;
    this.conventionModal = false;
  }

  onSaved(message: string) {
    this.closeModals();
    this.modalService.showSuccessMessage(message);
    this.load();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
